import React from 'react';
import { Plus, Trash2, CalendarClock } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import RichTextEditor from './RichTextEditor';

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Admin editor for the "Assignment" activity: instructions, scenarios, deliverables, deadline.
export default function AssignmentEditor({ activity, onChange }) {
  const a = activity.assignment || {};
  const scenarios = a.scenarios || [];
  const set = (patch) => onChange('assignment', { ...a, ...patch });
  const setScenario = (i, patch) => set({ scenarios: scenarios.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const id = activity.activity_id;
  return (
    <div className="mt-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.03] p-4 space-y-5" data-testid={`assignment-editor-${id}`}>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label className="text-zinc-300 text-sm flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-amber-400" />Due date & time *</Label>
          <Input type="datetime-local" value={toLocalInput(activity.due_at)}
            onChange={(e) => onChange('due_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
            className="bg-zinc-800 border-zinc-700 text-white mt-1 w-64" data-testid={`assignment-due-${id}`} />
        </div>
        <p className="text-xs text-zinc-500 pb-2">Submissions close automatically at the deadline. Learners get in-app reminders when it's published, 3 days and 1 day before, and when it's overdue.</p>
      </div>
      <div>
        <Label className="text-zinc-300 text-sm">Instructions</Label>
        <div className="mt-1"><RichTextEditor value={a.instructions_html || ''} onChange={(html) => set({ instructions_html: html })} placeholder="What learners must do, word count, format, referencing..." /></div>
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300 text-sm">Scenarios / case studies</Label>
          <Button type="button" size="sm" variant="outline" onClick={() => set({ scenarios: [...scenarios, { title: `Scenario ${scenarios.length + 1}`, body_html: '' }] })}
            className="border-zinc-700 text-zinc-200 h-8" data-testid={`assignment-add-scenario-${id}`}><Plus className="h-3.5 w-3.5 mr-1" />Add scenario</Button>
        </div>
        {scenarios.map((s, i) => (
          <div key={i} className="rounded-lg border border-zinc-800 p-3 space-y-2" data-testid={`assignment-scenario-${id}-${i}`}>
            <div className="flex gap-2">
              <Input value={s.title} onChange={(e) => setScenario(i, { title: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white h-9" />
              <Button type="button" size="icon" variant="ghost" onClick={() => set({ scenarios: scenarios.filter((_, j) => j !== i) })} className="text-red-400 h-9 w-9"><Trash2 className="h-4 w-4" /></Button>
            </div>
            <RichTextEditor value={s.body_html || ''} onChange={(html) => setScenario(i, { body_html: html })} placeholder="Describe the situation the learner must respond to..." />
          </div>
        ))}
      </div>
      <div>
        <Label className="text-zinc-300 text-sm">What to submit / marking guidance</Label>
        <div className="mt-1"><RichTextEditor value={a.deliverables_html || ''} onChange={(html) => set({ deliverables_html: html })} placeholder="e.g. A 1,500-word report in Word or PDF covering..." /></div>
      </div>
    </div>
  );
}
